// ── Monitor wall geometry ───────────────────────────────────────────────────
//
// The wall planner lays monitors out in millimetres. Each wall has its own
// origin and rotation, so a monitor's position is stored in wall-local
// coordinates and converted to world coordinates for drawing.
//
// The types below are deliberately structural and minimal - anything with the
// right fields satisfies them, so this module knows nothing about presets,
// names or ids.

export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A monitor's visible panel: position and size, bezels excluded. */
export interface PanelBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A panel plus its bezel widths: left, top, right, bottom. */
export interface BezelledBox extends PanelBox {
  bl: number;
  bt: number;
  br: number;
  bb: number;
}

/** A wall's placement in world space. */
export interface WallPlacement {
  origin: { x: number; y: number };
  /** Degrees, clockwise. */
  rotation: number;
}

/** Fallback extent for an empty wall, so the view has something to frame. */
const EMPTY_BOUNDS: Bounds = { x: 0, y: 0, w: 1000, h: 500 };

/**
 * Bounding box of the visible panels only.
 *
 * This is the picture area - what content actually fills. Bezels sit outside
 * it, which is why it differs from totalBounds.
 */
export function visibleBounds(monitors: readonly PanelBox[]): Bounds {
  if (!monitors.length) return { ...EMPTY_BOUNDS };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const m of monitors) {
    minX = Math.min(minX, m.x);
    minY = Math.min(minY, m.y);
    maxX = Math.max(maxX, m.x + m.w);
    maxY = Math.max(maxY, m.y + m.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/**
 * Bounding box including bezels - the physical footprint.
 *
 * This is what has to fit on the wall and what the mounting hardware spans.
 */
export function totalBounds(monitors: readonly BezelledBox[]): Bounds {
  if (!monitors.length) return { ...EMPTY_BOUNDS };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const m of monitors) {
    minX = Math.min(minX, m.x - m.bl);
    minY = Math.min(minY, m.y - m.bt);
    maxX = Math.max(maxX, m.x + m.w + m.br);
    maxY = Math.max(maxY, m.y + m.h + m.bb);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Rotation as cosine and sine, computed once per conversion. */
export function wallMatrix(wall: WallPlacement): { c: number; s: number } {
  const t = (wall.rotation * Math.PI) / 180;
  return { c: Math.cos(t), s: Math.sin(t) };
}

/** Wall-local point to world coordinates. */
export function wallToWorld(wall: WallPlacement, lx: number, ly: number): { x: number; y: number } {
  const { c, s } = wallMatrix(wall);
  return {
    x: wall.origin.x + lx * c - ly * s,
    y: wall.origin.y + lx * s + ly * c,
  };
}

/** World point to wall-local coordinates. The inverse of wallToWorld. */
export function worldToWall(wall: WallPlacement, wx: number, wy: number): { x: number; y: number } {
  const { c, s } = wallMatrix(wall);
  const dx = wx - wall.origin.x;
  const dy = wy - wall.origin.y;
  return { x: dx * c + dy * s, y: -dx * s + dy * c };
}

/**
 * Fold a rotation into (-180, 180].
 *
 * Dragging a wall's rotation handle accumulates without bound, so this keeps
 * the stored value readable. The bounds are asymmetric: 180 stays 180 and -180
 * stays -180, because neither loop condition is met at exactly those values.
 */
export function normRot(r: number): number {
  let out = r;
  while (out > 180) out -= 360;
  while (out < -180) out += 360;
  return out;
}
